import {
  TaskStatus,
  GroupedTasks as GroupedTasks,
  TaskArgument,
  ChallengeCampaign,
  ChallengeTask,
  Campaign,
  CampaignType,
  ChallengeCampaignEventState,
} from "../types";

export const getAirdropHeaderProjects = (campaigns: ChallengeCampaign[]) => {
  return campaigns.map((campaign) => {
    return {
      img: campaign.metadata.projectImage,
      id: campaign.id,
    };
  });
};

export const getActiveTasks = (
  campaign: ChallengeCampaign,
  tasks: TaskStatus[]
) => {
  const activeTasks = campaign?.tasks?.filter(
    (task) => task.isEnabled || task.taskName === "otc_points"
  );

  const activeTasksStatus = tasks.filter((task) =>
    activeTasks?.some((t) => t.id === task.taskGuid)
  );

  return activeTasksStatus;
};

export const groupTasksByType = (activeTasksStatus: TaskStatus[]) => {
  const taskTypeOrder = [
    "On-Chain Challenges",
    "Periodic Challenges",
    "Reactor Quests",
    "Social Challenges",
    "Game Challenges",
    "Special Challenges",
    "Quiz Challenges",
    "Engage Challenges",
    "Game of Life Challenges",
  ];

  const groupedTasksArray = Object.values(
    activeTasksStatus?.reduce(
      (acc, task) => {
        const taskTypeIndex = taskTypeOrder.indexOf(task.type);
        if (taskTypeIndex !== -1) {
          if (acc[taskTypeIndex]) {
            acc[taskTypeIndex].push(task);
          } else {
            acc[taskTypeIndex] = [task];
          }
        }
        return acc;
      },
      {} as Record<number, TaskStatus[]>
    ) ?? {}
  );
  return groupedTasksArray;
};

export const groupAndFilterByActiveTasks = (
  campaign: ChallengeCampaign,
  tasks: TaskStatus[]
): GroupedTasks => {
  return groupTasksByType(getActiveTasks(campaign, tasks));
};

// export const airdropsClasses = (airdropClasses: IAirdropClasses) => {
//   const classes = [
//     {
//       title: airdropClasses?.project.title,
//       description: airdropClasses?.project.shortDescription,
//       children: <Image src={''} alt={'Project Image'} width={0} height={0} />,
//       points: airdropClasses?.project.points,
//       moreInfoContent: airdropClasses?.project.description,
//       icon: airdropClasses?.project.icon,
//     },
//     {
//       title: airdropClasses?.partners.title,
//       description: airdropClasses?.partners.shortDescription,

//       points: airdropClasses?.partners.points,
//       moreInfoContent: airdropClasses?.partners.description,
//       icon: airdropClasses?.partners.icon,
//     },
//     {
//       title: airdropClasses?.ecosystem.title,
//       description: airdropClasses?.ecosystem.shortDescription,
//       points: airdropClasses?.ecosystem.points,
//       moreInfoContent: airdropClasses?.ecosystem.description,
//       icon: airdropClasses?.ecosystem.icon,
//     },
//   ];

//   return classes;
// };

export const setTaskGuidAndName = (taskGuid: string, taskName: string) => {
  window.localStorage.setItem("taskGuid", taskGuid);
  window.localStorage.setItem("taskName", taskName);
};

export const consumeTaskGuidAndName = () => {
  const taskGuid = window.localStorage.getItem("taskGuid");
  const taskName = window.localStorage.getItem("taskName");

  window.localStorage.removeItem("taskGuid");
  window.localStorage.removeItem("taskName");
  return { taskGuid, taskName };
};

export const getArgument = (
  args: TaskArgument[],
  argumentName: string
): TaskArgument | undefined => {
  const argument = args.find((arg) => arg.name === argumentName);
  if (!argument || !argument.value) {
    return undefined;
  }

  return argument;
};

export const getAirdropArgumentValue = (
  args: TaskArgument[],
  argumentName: string
): string | undefined => {
  const argument = getArgument(args, argumentName);
  return argument?.value;
};

export const getMaxPoints = (campaignTask: ChallengeTask) => {
  let maxPoints: number = Number(campaignTask.points);

  const parsedTiers = JSON.parse(
    getAirdropArgumentValue(campaignTask.arguments, "pointsTiers") || "{}"
  );

  const maxTierKey =
    Object.keys(parsedTiers).sort((a, b) => Number(b) - Number(a))[0] || "0";

  const maxTierPoints = parsedTiers[maxTierKey] || 0;

  if (/^\d+$/.test(maxTierPoints?.toString()) && maxTierPoints > 0) {
    maxPoints = Number(maxTierPoints);
  }

  return maxPoints;
};

export const getInstancesTimeframe = (
  campaignTask: ChallengeTask
): "Daily" | "Weekly" | "Monthly" | null => {
  const instances = JSON.parse(
    getAirdropArgumentValue(campaignTask.arguments, "instances") || "[]"
  ) as { startTime: string; endTime: string }[];
  console.log("instances for ", campaignTask.title, instances);

  if (!instances || instances.length < 2) {
    console.log("no instances for ", campaignTask.title);
    return null;
  }

  // Get the time difference between two consecutive instances in milliseconds
  if (!instances[0] || !instances[1]) return null;
  const firstInstance = new Date(instances[0].startTime);
  const secondInstance = new Date(instances[1].startTime);
  const timeDifference = secondInstance.getTime() - firstInstance.getTime();

  // Convert milliseconds to days
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
  console.log("daysDifference", daysDifference);

  if (daysDifference <= 1) return "Daily";
  if (daysDifference <= 7) return "Weekly";
  return "Monthly";
};

export const getCampaignIdAndGroupId = (
  campaignId: string
): [string, string | undefined] => {
  const parts = campaignId.split("-");
  if (parts.length === 1) {
    return [parts[0]!, undefined];
  }
  const groupId = parts.pop(); // Get the last segment as groupId
  const questId = parts.join("-"); // Join remaining segments with '-'
  return [questId, groupId];
};

export const getCampaignFromChallengeType = (
  campaigns: ChallengeCampaign[]
): Campaign[] => {
  console.log(campaigns);
  return campaigns.map((campaign) => {
    return {
      id: campaign.id,
      type: CampaignType.CHALLENGE,
      campaignGroup: campaign.campaignGroup,
      name: campaign.metadata?.name,
      description: campaign.metadata?.description,
      imageUrl: campaign.metadata?.imageUrl,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
      allocation: campaign.metadata?.totalAmount,
      token: campaign.metadata?.token,
      totalUsers: campaign.metadata?.totalUsers,
      topUsers: campaign.metadata?.topUsers,
      chainId: campaign.chainId,
      totalQuests: campaign.metadata?.totalQuests,
      isEnabled: campaign.isEnabled,
      tokenImageUrl: campaign.metadata?.token,
      tokenSymbol: campaign.metadata?.tokenSymbol,
    };
  });
};

/**
 * Extract dependentOn GUIDs from task arguments
 */
export const getTaskDependencies = (task: ChallengeTask): string[] => {
  const dependentOnArg = task.arguments?.find(
    (arg) => arg.name === "dependentOn"
  );
  if (!dependentOnArg?.value) return [];
  try {
    return JSON.parse(dependentOnArg.value);
  } catch {
    return [];
  }
};

/**
 * Check if all dependent tasks are completed
 */
export const areTaskDependenciesMet = (
  task: ChallengeTask,
  taskStatuses: TaskStatus[]
): boolean => {
  const dependencies = getTaskDependencies(task);
  if (dependencies.length === 0) return true;

  return dependencies.every((depGuid) => {
    const depTask = taskStatuses.find((t) => t.taskGuid === depGuid);
    return depTask?.status === ChallengeCampaignEventState.SUCCESSFUL;
  });
};
